import { modoDev, msalInstance, scopesApi } from '@/lib/auth';
import { obtenerUsuarioActual } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { LogOut, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LogoAdelante } from './LogoAdelante';

const linkBase = 'text-sm transition-colors';
const linkInactive = 'text-zinc-400 hover:text-white';
const linkActive = 'text-white';

interface Props {
  /** Nombre de la app que aparece a la derecha del logo. Default: "App Starter". */
  nombreApp?: string;
}

export function HeaderAdelante({ nombreApp = 'App Starter' }: Props) {
  const usuario = obtenerUsuarioActual();
  const iniciales = usuario?.iniciales ?? '?';
  const nombreCompleto = usuario?.name ?? 'Sin sesión';

  return (
    <header className="bg-[#0A0A0A] text-white">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <LogoAdelante altura={20} />
          <span className="text-sm font-bold tracking-wider">ADELANTE.</span>
          <span className="text-zinc-700">|</span>
          <span className="text-sm text-zinc-300">{nombreApp}</span>
        </div>

        <nav className="flex items-center gap-6">
          <NavLink
            to="/resumen"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Resumen
          </NavLink>
          <NavLink
            to="/ingresos"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Ingresos
          </NavLink>
          <NavLink
            to="/detalle"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Detalle
          </NavLink>
          <NavLink
            to="/neta"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Utilidad Neta
          </NavLink>
          <NavLink
            to="/movimientos"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Movimientos
          </NavLink>
          <NavLink
            to="/indirectos"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Indirectos
          </NavLink>
          <NavLink
            to="/obras"
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Obras
          </NavLink>
        </nav>

        <MenuUsuario
          iniciales={iniciales}
          nombre={nombreCompleto}
          email={usuario?.username ?? ''}
        />
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Menú del avatar — dropdown con info del usuario, recargar token y cerrar sesión
// ---------------------------------------------------------------------------

interface PropsMenu {
  iniciales: string;
  nombre: string;
  email: string;
}

function MenuUsuario({ iniciales, nombre, email }: PropsMenu) {
  const [open, setOpen] = useState(false);
  const [recargando, setRecargando] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside para cerrar el panel
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /**
   * Fuerza obtener un token fresco del API. Útil cuando MSAL devuelve
   * `timed_out`, `interaction_required` o el token cacheado quedó stale
   * (la app empieza a recibir 401 sin razón aparente).
   * Si el silent refresh falla, cae al redirect y vuelve a loguear.
   */
  async function handleRecargarToken() {
    if (modoDev || !msalInstance) {
      setOpen(false);
      return;
    }
    setRecargando(true);
    try {
      const cuenta = msalInstance.getAllAccounts()[0];
      if (!cuenta) {
        await msalInstance.loginRedirect({ scopes: scopesApi });
        return;
      }
      await msalInstance.acquireTokenSilent({
        scopes: scopesApi,
        account: cuenta,
        forceRefresh: true,
      });
      // Token fresco en cache; recargar la pantalla para que los hooks lo usen.
      window.location.reload();
    } catch {
      // Silent falló — forzar interacción
      await msalInstance.acquireTokenRedirect({ scopes: scopesApi });
    } finally {
      setRecargando(false);
    }
  }

  function handleCerrarSesion() {
    if (modoDev || !msalInstance) {
      setOpen(false);
      return;
    }
    const cuenta = msalInstance.getAllAccounts()[0];
    msalInstance.logoutRedirect({
      account: cuenta,
      postLogoutRedirectUri: window.location.origin,
    });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground transition-opacity hover:opacity-80"
        aria-label={`Usuario: ${nombre}`}
        title={nombre}
      >
        {iniciales}
      </button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-64 overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-900 shadow-lg">
          {/* Encabezado: datos del usuario */}
          <div className="border-b border-zinc-100 px-3 py-2">
            <p className="truncate text-sm font-semibold" title={nombre}>
              {nombre}
            </p>
            {email && (
              <p className="truncate text-xs text-zinc-500" title={email}>
                {email}
              </p>
            )}
            {modoDev && (
              <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                Modo dev
              </span>
            )}
          </div>

          {/* Acciones */}
          <button
            type="button"
            onClick={handleRecargarToken}
            disabled={recargando || modoDev}
            className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={cn('mt-0.5 h-3.5 w-3.5', recargando && 'animate-spin')} />
            <div className="flex-1">
              <div>Recargar token</div>
              <div className="text-[11px] text-zinc-500">si la app se cuelga</div>
            </div>
          </button>

          <button
            type="button"
            onClick={handleCerrarSesion}
            disabled={modoDev}
            className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-sm text-destructive hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LogOut className="h-3.5 w-3.5" />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
